import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, logoutUser } from '../../utils/api';
import { ThemeProvider } from '@mui/material/styles';
import { 
  Box, 
  Button, 
  Chip, 
  Paper, 
  Typography, 
  TextField, 
  Grid, 
  Card, 
  CardContent,
  Container,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  Tooltip,
  Stack,
  Avatar,
  ButtonBase,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  Emergency as EmergencyIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  AttachMoney as AttachMoneyIcon,
  TrendingUp as TrendingUpIcon,
  LocalHospital as LocalHospitalIcon,
  AccessTime as AccessTimeIcon,
  Work as WorkIcon,
  ArrowForward as ArrowForwardIcon,
  PersonOutline as PersonOutlineIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  School as SchoolIcon,
  CardMembership as CardMembershipIcon,
  Logout as LogoutIcon,
  Reviews as ReviewsIcon,
  Star as StarIcon
} from '@mui/icons-material';
import muiTheme from '../../theme/muiTheme';
import './NurseDashboard.css';
import './NurseDashboard_ColorEnhancement.css';

// Reusable StatCard Component - UNIFORM SIZE with POP EFFECT
const StatCard = ({ icon: Icon, label, value, color = '#1E40AF', gradient = false }) => (
  <Card 
    className="stat-card-modern"
    sx={{
      height: '100%',
      minHeight: 160, // UNIFORM HEIGHT
      display: 'flex',
      flexDirection: 'column',
      background: gradient 
        ? `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`
        : 'var(--bg-card)',
      borderRadius: 3,
      border: gradient ? 'none' : '1px solid var(--border-color)',
      boxShadow: gradient 
        ? `0 4px 12px -4px ${color}40`
        : '0 2px 8px rgba(0, 0, 0, 0.08)',
      position: 'relative',
      overflow: 'hidden',
      transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
      '@media (prefers-reduced-motion: reduce)': {
        transition: 'none'
      },
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: gradient 
          ? `0 12px 24px -8px ${color}66`
          : '0 8px 16px rgba(0, 0, 0, 0.12)',
        borderColor: gradient ? 'transparent' : color,
      }
    }}
  >
    <CardContent sx={{ 
      p: 3, 
      flex: 1, 
      display: 'flex', 
      flexDirection: 'column',
      justifyContent: 'space-between',
      position: 'relative',
      zIndex: 1
    }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ 
          bgcolor: gradient ? 'rgba(255, 255, 255, 0.2)' : `${color}15`,
          borderRadius: 2.5,
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: gradient ? 'blur(10px)' : 'none'
        }}>
          <Icon sx={{ 
            fontSize: 32, 
            color: gradient ? '#FFFFFF' : color,
            filter: gradient ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' : 'none'
          }} />
        </Box>
      </Box>
      <Box>
        <Typography 
          variant="body2" 
          sx={{ 
            color: gradient ? 'rgba(255, 255, 255, 0.9)' : 'var(--text-secondary)',
            textTransform: 'uppercase',
            letterSpacing: 1,
            fontWeight: 600,
            fontSize: '0.75rem',
            mb: 1
          }}
        >
          {label}
        </Typography>
        <Typography 
          variant="h3" 
          sx={{ 
            color: gradient ? '#FFFFFF' : 'var(--text-primary)',
            fontWeight: 700,
            fontSize: { xs: '2rem', sm: '2.5rem' },
            lineHeight: 1.2
          }}
        >
          {value}
        </Typography>
      </Box>
    </CardContent>
    {gradient && (
      <Box sx={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: '60%',
        height: '100%',
        background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)',
        pointerEvents: 'none'
      }} />
    )}
  </Card>
);

// Modern Tab Button Component
const TabButton = ({ icon: Icon, label, active, onClick }) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      flex: { xs: '1 1 auto', sm: '0 1 auto' },
      minWidth: { xs: 'auto', sm: 140 },
      height: 56,
      px: { xs: 2, sm: 3 },
      borderRadius: 2,
      background: active ? 'var(--primary-gradient)' : 'transparent',
      color: active ? '#FFFFFF' : 'var(--text-primary)',
      fontWeight: active ? 600 : 500,
      fontSize: '0.95rem',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      border: active ? 'none' : '1px solid var(--border-color)',
      '&:hover': {
        background: active ? 'var(--primary-gradient-hover)' : 'var(--bg-secondary)',
        transform: 'translateY(-2px)',
        boxShadow: active ? 'var(--shadow-colored)' : 'var(--shadow-md)',
      }
    }}
  >
    <Icon sx={{ mr: { xs: 0, sm: 1 }, fontSize: 20 }} />
    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
      {label}
    </Box>
  </ButtonBase>
);

const NurseDashboard = () => {
  const navigate = useNavigate();
  const [nurseData, setNurseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [emergencyRequests, setEmergencyRequests] = useState([]);
  const [earningsToday, setEarningsToday] = useState(0);
  const [earningsTotal, setEarningsTotal] = useState(0);
  const [profileData, setProfileData] = useState({
    name: '',
    phone: '',
    specialization: '',
    yearsOfExperience: 0,
    shift: 'morning',
    education: [],
    certifications: []
  });
  const [availability, setAvailability] = useState({
    isAvailable: true,
    currentStatus: 'available'
  });
  const [updating, setUpdating] = useState(false);
  const [availabilityUpdating, setAvailabilityUpdating] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark' || (!savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches);
  });

  // Initialize dark mode
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Monitor theme changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
      setIsDarkMode(isDark);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });
    return () => observer.disconnect();
  }, []);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    document.documentElement.setAttribute('data-theme', !isDarkMode ? 'dark' : 'light');
    localStorage.setItem('theme', !isDarkMode ? 'dark' : 'light');
  };

  useEffect(() => {
    fetchNurseData();
    fetchEmergencyRequests();
    
    // Auto-refresh requests every 30 seconds to catch new patient orders
    const refreshInterval = setInterval(() => {
      fetchEmergencyRequests();
    }, 30000);
    
    return () => clearInterval(refreshInterval);
  }, []);
  
  // Fetch reviews when Reviews tab is active
  useEffect(() => {
    if (activeTab === 3) {
      fetchReviews();
    }
  }, [activeTab]);

  const fetchNurseData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/nurse/dashboard');
      if (response.data.success) {
        setNurseData(response.data.data.nurse);
        setEmergencyRequests(response.data.data.emergencyRequests || []);
        
        setProfileData({
          name: response.data.data.nurse.name,
          phone: response.data.data.nurse.phone,
          specialization: response.data.data.nurse.nurseDetails?.specialization || '',
          yearsOfExperience: response.data.data.nurse.nurseDetails?.yearsOfExperience || 0,
          shift: response.data.data.nurse.nurseDetails?.shift || 'morning',
          education: response.data.data.nurse.nurseDetails?.education || [],
          certifications: response.data.data.nurse.nurseDetails?.certifications || []
        });

        setAvailability({
          isAvailable: response.data.data.nurse.nurseDetails?.isAvailable || true,
          currentStatus: response.data.data.nurse.nurseDetails?.currentStatus || 'available'
        });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching nurse data');
      console.error('Error fetching nurse data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmergencyRequests = async () => {
    try {
      const response = await api.get('/emergency-nurse/nurse/requests');
      if (response.data.success) {
        const reqs = response.data.data.requests || [];
        setEmergencyRequests(reqs);
        const now = new Date();
        const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const today = reqs.filter(r => r.status === 'completed' && r.completedAt && new Date(r.completedAt) >= startOfDay);
        const sumToday = today.reduce((s, r) => s + (r.nursePayout || 0), 0);
        const sumTotal = reqs.filter(r => r.status === 'completed').reduce((s, r) => s + (r.nursePayout || 0), 0);
        setEarningsToday(sumToday);
        setEarningsTotal(sumTotal);
      }
    } catch (err) {
      console.error('Error fetching emergency requests:', err);
    }
  };
  
  // Fetch anonymized reviews
  const fetchReviews = async () => {
    try {
      setReviewsLoading(true);
      const response = await api.get('/emergency-nurse/nurse/reviews');
      if (response.data.success) {
        // Anonymize reviews - remove all identifying information
        const anonymized = (response.data.data.reviews || []).map(review => ({
          _id: review._id,
          rating: review.rating,
          comment: review.comment,
          createdAt: review.createdAt,
          // Explicitly remove any identifying fields
          patientId: undefined,
          patientName: undefined,
          patientEmail: undefined,
          patientPhone: undefined,
          reviewer: 'Anonymous Client' // Always show as anonymous
        }));
        setReviews(anonymized);
      }
    } catch (err) {
      console.error('Error fetching reviews:', err);
      // If endpoint doesn't exist yet, show empty state
      setReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  };
  
  // Handle logout
  const handleLogout = async () => {
    try {
      await logoutUser();
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
      // Force logout even if API call fails
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/login');
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      setUpdating(true);
      const response = await api.put('/nurse/profile', {
        name: profileData.name,
        phone: profileData.phone,
        nurseDetails: {
          specialization: profileData.specialization,
          yearsOfExperience: profileData.yearsOfExperience,
          shift: profileData.shift,
          education: profileData.education,
          certifications: profileData.certifications
        }
      });

      if (response.data.success) {
        setNurseData(response.data.data.nurse);
        alert('Profile updated successfully!');
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Error updating profile');
      console.error('Error updating profile:', err);
    } finally {
      setUpdating(false);
    }
  };

  // NEW: Handle availability toggle in header
  const handleAvailabilityToggle = async (checked) => {
    try {
      setAvailabilityUpdating(true);
      const newAvailability = {
        isAvailable: checked,
        currentStatus: checked ? 'available' : 'offline'
      };
      
      const response = await api.put('/nurse/availability', newAvailability);

      if (response.data.success) {
        setAvailability(newAvailability);
        setNurseData(response.data.data.nurse);
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Error updating availability');
      console.error('Error updating availability:', err);
      // Revert toggle on error
      setAvailability(prev => prev);
    } finally {
      setAvailabilityUpdating(false);
    }
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      const response = await api.put(`/emergency-nurse/nurse/accept/${requestId}`);
      if (response.data.success) {
        await fetchEmergencyRequests();
        alert('Emergency request accepted successfully!');
      }
    } catch (err) {
      alert('Failed to accept request. Please try again.');
      console.error('Error accepting request:', err);
    }
  };

  const handleCompleteRequest = async (requestId) => {
    const notes = prompt('Please add any notes about the service provided:');
    if (notes === null) return;

    try {
      const response = await api.put(`/emergency-nurse/nurse/complete/${requestId}`, {
        notes
      });
      if (response.data.success) {
        await fetchEmergencyRequests();
        alert('Emergency request completed successfully!');
      }
    } catch (err) {
      alert('Failed to complete request. Please try again.');
      console.error('Error completing request:', err);
    }
  };

  const addEducation = () => {
    setProfileData(prev => ({
      ...prev,
      education: [...prev.education, { degree: '', institution: '', year: '' }]
    }));
  };

  const updateEducation = (index, field, value) => {
    setProfileData(prev => ({
      ...prev,
      education: prev.education.map((edu, i) => 
        i === index ? { ...edu, [field]: value } : edu
      )
    }));
  };

  const removeEducation = (index) => {
    setProfileData(prev => ({
      ...prev,
      education: prev.education.filter((_, i) => i !== index)
    }));
  };

  const addCertification = () => {
    setProfileData(prev => ({
      ...prev,
      certifications: [...prev.certifications, '']
    }));
  };

  const updateCertification = (index, value) => {
    setProfileData(prev => ({
      ...prev,
      certifications: prev.certifications.map((cert, i) => 
        i === index ? value : cert
      )
    }));
  };

  const removeCertification = (index) => {
    setProfileData(prev => ({
      ...prev,
      certifications: prev.certifications.filter((_, i) => i !== index)
    }));
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'critical': return '#EF4444';
      case 'high': return '#F59E0B';
      case 'medium': return '#3B82F6';
      case 'low': return '#10B981';
      default: return '#64748B';
    }
  };

  if (loading) {
    return (
      <Box className="nurse-dashboard-modern" sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: 'var(--bg-primary)'
      }}>
        <CircularProgress size={60} sx={{ color: '#1E40AF' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert>
      </Container>
    );
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <Box className={`nurse-dashboard-modern ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
        <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
          {/* Premium Header Section with Availability Toggle */}
          <Paper 
            elevation={0}
            sx={{
              background: 'var(--header-gradient)',
              backdropFilter: 'blur(20px)',
              border: '1px solid var(--border-color)',
              borderRadius: 4,
              p: { xs: 3, sm: 4 },
              mb: 4,
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Decorative background elements */}
            <Box sx={{
              position: 'absolute',
              top: -50,
              right: -50,
              width: 200,
              height: 200,
              background: 'radial-gradient(circle, rgba(30, 64, 175, 0.15) 0%, transparent 70%)',
              borderRadius: '50%',
              pointerEvents: 'none'
            }} />
            
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={3} 
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              sx={{ position: 'relative', zIndex: 1 }}
            >
              {/* Left: Nurse Identity + Availability Toggle */}
              <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, width: '100%' }}>
                <Avatar
                  sx={{
                    width: { xs: 56, sm: 72 },
                    height: { xs: 56, sm: 72 },
                    bgcolor: '#1E40AF',
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    fontWeight: 700,
                    boxShadow: '0 8px 16px -4px rgba(30, 64, 175, 0.3)'
                  }}
                >
                  {nurseData?.name?.charAt(0)}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography 
                    variant="h5" 
                    className="nurse-name"
                    sx={{ 
                      fontWeight: 800, 
                      color: 'var(--text-primary)',
                      fontSize: { xs: '1.4rem', sm: '1.75rem' },
                      mb: 0.5,
                      background: 'var(--gradient-primary)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}
                  >
                    Welcome, {nurseData?.name}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    className="nurse-role"
                    sx={{ 
                      color: 'var(--text-secondary)',
                      fontSize: { xs: '0.8rem', sm: '0.9rem' },
                      mb: 1.5,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {nurseData?.nurseDetails?.specialization || 'General'} Nurse
                  </Typography>
                  
                  {/* Availability Badge + Toggle */}
                  <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                    <Chip 
                      icon={<LocalHospitalIcon />}
                      label={availability.isAvailable ? 'Available' : 'Unavailable'} 
                      size="small"
                      className={availability.isAvailable ? 'status-completed' : 'status-cancelled'}
                      sx={{
                        fontWeight: 700,
                        border: 'none !important',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                      }}
                    />
                    
                    {/* Availability Toggle Switch */}
                    <Tooltip title={availabilityUpdating ? 'Updating...' : 'Toggle Availability'}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                          {availabilityUpdating ? 'Saving...' : 'Toggle'}
                        </Typography>
                        <Switch
                          checked={availability.isAvailable}
                          onChange={(e) => handleAvailabilityToggle(e.target.checked)}
                          disabled={availabilityUpdating}
                          size="small"
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#10B981'
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#10B981'
                            },
                            '& .MuiSwitch-track': {
                              backgroundColor: '#EF4444'
                            }
                          }}
                        />
                        {availabilityUpdating && (
                          <CircularProgress size={16} sx={{ color: '#1E40AF' }} />
                        )}
                      </Box>
                    </Tooltip>
                  </Stack>
                </Box>
              </Stack>

              {/* Right: Theme Toggle + Logout */}
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Tooltip title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
                  <IconButton 
                    onClick={toggleDarkMode}
                    sx={{
                      bgcolor: 'var(--toggle-bg)',
                      color: 'var(--primary-color)',
                      width: { xs: 48, sm: 56 },
                      height: { xs: 48, sm: 56 },
                      boxShadow: 'var(--shadow-md)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        bgcolor: 'var(--toggle-bg-hover)',
                        transform: 'scale(1.05) rotate(15deg)',
                        boxShadow: 'var(--shadow-lg)'
                      }
                    }}
                  >
                    {isDarkMode ? <Brightness7Icon /> : <Brightness4Icon />}
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Logout">
                  <IconButton 
                    onClick={handleLogout}
                    sx={{
                      bgcolor: 'rgba(239, 68, 68, 0.1)',
                      color: '#EF4444',
                      width: { xs: 48, sm: 56 },
                      height: { xs: 48, sm: 56 },
                      boxShadow: 'var(--shadow-md)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        bgcolor: 'rgba(239, 68, 68, 0.2)',
                        transform: 'scale(1.05)',
                        boxShadow: 'var(--shadow-lg)'
                      }
                    }}
                  >
                    <LogoutIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Paper>

          {/* Modern Tab Navigation - 3 Tabs Only (Dashboard | Profile | Requests) */}
          <Paper 
            elevation={0}
            sx={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border-color)',
              borderRadius: 3,
              p: 1,
              mb: 4,
              display: 'flex',
              gap: 1,
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                height: 6
              },
              '&::-webkit-scrollbar-track': {
                background: 'var(--bg-secondary)',
                borderRadius: 3
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'var(--primary-color)',
                borderRadius: 3
              }
            }}
          >
            <TabButton icon={DashboardIcon} label="Dashboard" active={activeTab === 0} onClick={() => setActiveTab(0)} />
            <TabButton icon={PersonIcon} label="Profile" active={activeTab === 1} onClick={() => setActiveTab(1)} />
            <TabButton icon={EmergencyIcon} label="Requests" active={activeTab === 2} onClick={() => setActiveTab(2)} />
            <TabButton icon={ReviewsIcon} label="Reviews" active={activeTab === 3} onClick={() => setActiveTab(3)} />
          </Paper>

          {/* Tab Content */}
          <Box>
            {/* Dashboard Tab */}
            {activeTab === 0 && (
              <Box>
                {/* KPI Cards - Uniform Size Grid */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatCard 
                      icon={EmergencyIcon}
                      label="Total Requests"
                      value={emergencyRequests.length}
                      color="#1E40AF"
                      gradient
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatCard 
                      icon={AccessTimeIcon}
                      label="Active Requests"
                      value={emergencyRequests.filter(req => ['pending', 'accepted', 'in_progress'].includes(req.status)).length}
                      color="#F59E0B"
                      gradient
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatCard 
                      icon={CheckCircleIcon}
                      label="Completed"
                      value={emergencyRequests.filter(req => req.status === 'completed').length}
                      color="#10B981"
                      gradient
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatCard 
                      icon={WorkIcon}
                      label="Experience"
                      value={`${nurseData?.nurseDetails?.yearsOfExperience || 0} years`}
                      color="#8B5CF6"
                      gradient
                    />
                  </Grid>
                </Grid>

                {/* Earnings Cards - Uniform Size (160px) */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={6}>
                    <Card 
                      className="earnings-card-modern"
                      sx={{
                        height: '100%',
                        minHeight: 160, // UNIFORM with stat cards
                        background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                        borderRadius: 3,
                        border: 'none',
                        boxShadow: '0 4px 12px -4px rgba(16, 185, 129, 0.4)',
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                        '@media (prefers-reduced-motion: reduce)': {
                          transition: 'none'
                        },
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 12px 24px -8px rgba(16, 185, 129, 0.5)'
                        }
                      }}
                    >
                      <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Box sx={{
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                            borderRadius: 2.5,
                            p: 1.5,
                            backdropFilter: 'blur(10px)'
                          }}>
                            <AttachMoneyIcon sx={{ fontSize: 32, color: '#FFFFFF' }} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 0.5, fontWeight: 600 }}>
                              Today's Earnings
                            </Typography>
                            <Typography variant="h4" sx={{ color: '#FFFFFF', fontWeight: 700 }}>
                              ৳ {earningsToday.toLocaleString()}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card 
                      className="earnings-card-modern"
                      sx={{
                        height: '100%',
                        minHeight: 160, // UNIFORM with stat cards
                        background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                        borderRadius: 3,
                        border: 'none',
                        boxShadow: '0 4px 12px -4px rgba(59, 130, 246, 0.4)',
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                        '@media (prefers-reduced-motion: reduce)': {
                          transition: 'none'
                        },
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 12px 24px -8px rgba(59, 130, 246, 0.5)'
                        }
                      }}
                    >
                      <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Box sx={{
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                            borderRadius: 2.5,
                            p: 1.5,
                            backdropFilter: 'blur(10px)'
                          }}>
                            <TrendingUpIcon sx={{ fontSize: 32, color: '#FFFFFF' }} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 0.5, fontWeight: 600 }}>
                              Total Earnings
                            </Typography>
                            <Typography variant="h4" sx={{ color: '#FFFFFF', fontWeight: 700 }}>
                              ৳ {earningsTotal.toLocaleString()}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Recent Emergency Requests Panel */}
                <Card 
                  sx={{
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 3,
                    minHeight: 300
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <EmergencyIcon sx={{ fontSize: 28, color: '#1E40AF' }} />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                          Recent Emergency Requests
                        </Typography>
                      </Stack>
                      {emergencyRequests.length > 0 && (
                        <Button 
                          size="small"
                          endIcon={<ArrowForwardIcon />}
                          onClick={() => setActiveTab(2)}
                          sx={{
                            color: '#1E40AF',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                              background: 'rgba(30, 64, 175, 0.08)'
                            }
                          }}
                        >
                          View All
                        </Button>
                      )}
                    </Stack>

              {emergencyRequests.length === 0 ? (
                      <Box sx={{ 
                        textAlign: 'center', 
                        py: 6,
                        px: 2
                      }}>
                        <EmergencyIcon sx={{ fontSize: 64, color: 'var(--text-tertiary)', mb: 2, opacity: 0.5 }} />
                        <Typography variant="h6" sx={{ color: 'var(--text-secondary)', mb: 1 }}>
                          No Emergency Requests
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'var(--text-tertiary)' }}>
                          You'll see new requests here when patients need your help
                        </Typography>
                      </Box>
                    ) : (
                      <Grid container spacing={2.5}>
                        {emergencyRequests.slice(0, 6).map((request, index) => (
                          <Grid item xs={12} sm={6} md={4} key={request._id || index}>
                            <Card 
                              sx={{
                                height: '100%',
                                minHeight: 220,
                                borderLeft: `4px solid ${getUrgencyColor(request.urgency)}`,
                                borderRadius: 2,
                                background: 'var(--bg-card)',
                                border: '1px solid var(--border-color)',
                                borderLeftWidth: '4px',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                  transform: 'translateY(-4px)',
                                  boxShadow: 'var(--shadow-lg)'
                                }
                              }}
                            >
                              <CardContent sx={{ p: 2.5 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: '0.9rem' }}>
                                    #{request._id ? request._id.slice(-6).toUpperCase() : (index + 1).toString().padStart(6, '0')}
                                  </Typography>
                                  <Chip 
                                    label={request.urgency || 'Medium'} 
                                    size="small"
                                    sx={{
                                      height: 22,
                                      fontSize: '0.7rem',
                                      fontWeight: 700,
                                      bgcolor: `${getUrgencyColor(request.urgency)}15`,
                                      color: getUrgencyColor(request.urgency),
                                      border: `1px solid ${getUrgencyColor(request.urgency)}`,
                                      textTransform: 'uppercase'
                                    }}
                                  />
                                </Stack>
                                
                                <Stack spacing={1} sx={{ mb: 2.5 }}>
                                  <Stack direction="row" spacing={1}>
                                    <PersonOutlineIcon sx={{ fontSize: 16, color: 'var(--text-secondary)' }} />
                                    <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                                      {request.patientId?.name || 'Unknown Patient'}
                                    </Typography>
                                  </Stack>
                                  <Chip 
                                    label={request.status} 
                                    size="small"
                                    color={
                                      request.status === 'completed' ? 'success' :
                                      request.status === 'accepted' ? 'info' :
                                      request.status === 'pending' ? 'warning' : 'default'
                                    }
                                    sx={{ 
                                      height: 20, 
                                      fontSize: '0.7rem',
                                      width: 'fit-content',
                                      textTransform: 'capitalize'
                                    }}
                                  />
                                </Stack>

                                <Stack direction="row" spacing={1} sx={{ mt: 'auto' }}>
                        {request.status === 'pending' && (
                                    <Button 
                                      variant="contained" 
                                      size="small"
                                      fullWidth
                                      startIcon={<CheckCircleIcon />}
                            onClick={() => handleAcceptRequest(request._id)}
                                      sx={{
                                        background: 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        py: 0.75
                                      }}
                          >
                            Accept
                                    </Button>
                        )}
                        {request.status === 'accepted' && (
                                    <Button 
                                      variant="contained" 
                                      size="small"
                                      fullWidth
                                      color="success"
                                      startIcon={<CheckCircleIcon />}
                            onClick={() => handleCompleteRequest(request._id)}
                                      sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        py: 0.75
                                      }}
                          >
                            Complete
                                    </Button>
                                  )}
                                  <Button 
                                    variant="outlined" 
                                    size="small"
                                    fullWidth={request.status === 'completed'}
                                    startIcon={<VisibilityIcon />}
                                    sx={{
                                      borderColor: 'var(--border-color)',
                                      color: 'var(--text-primary)',
                                      textTransform: 'none',
                                      fontWeight: 600,
                                      fontSize: '0.75rem',
                                      py: 0.75,
                                      '&:hover': {
                                        borderColor: '#1E40AF',
                                        background: 'rgba(30, 64, 175, 0.08)'
                                      }
                                    }}
                                  >
                                    View
                                  </Button>
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </CardContent>
                </Card>
              </Box>
            )}

            {/* Profile Tab - REDESIGNED LAYOUT */}
            {activeTab === 1 && (
              <Grid container spacing={3}>
                {/* Left Column: Main Profile Form */}
                <Grid item xs={12} lg={8}>
                  <Card sx={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 3, height: '100%' }}>
                    <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 4 }}>
                        <PersonIcon sx={{ fontSize: 28, color: '#1E40AF' }} />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                          Personal Information
                        </Typography>
                      </Stack>
                      
                      <form onSubmit={handleProfileUpdate}>
                        <Grid container spacing={3}>
                          {/* 2-Column Layout for Desktop */}
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Full Name"
                  value={profileData.name}
                  onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                  required
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  background: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#FFFFFF',
                                  '& fieldset': { 
                                    borderColor: 'var(--border-color)',
                                    borderWidth: '1.5px'
                                  },
                                  '&:hover fieldset': { 
                                    borderColor: '#1E40AF',
                                    borderWidth: '1.5px'
                                  },
                                  '&.Mui-focused': {
                                    background: isDarkMode ? 'rgba(30, 41, 59, 0.7)' : '#FFFFFF',
                                    '& fieldset': { 
                                      borderColor: '#1E40AF',
                                      borderWidth: '2px'
                                    }
                                  }
                                },
                                '& .MuiInputLabel-root': { color: 'var(--text-secondary)' },
                                '& .MuiInputLabel-root.Mui-focused': { color: '#1E40AF' },
                                '& .MuiOutlinedInput-input': { 
                                  color: 'var(--text-primary)',
                                  background: 'transparent'
                                }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Phone Number"
                  type="tel"
                  value={profileData.phone}
                  onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                  required
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  background: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#FFFFFF',
                                  '& fieldset': { 
                                    borderColor: 'var(--border-color)',
                                    borderWidth: '1.5px'
                                  },
                                  '&:hover fieldset': { 
                                    borderColor: '#1E40AF',
                                    borderWidth: '1.5px'
                                  },
                                  '&.Mui-focused': {
                                    background: isDarkMode ? 'rgba(30, 41, 59, 0.7)' : '#FFFFFF',
                                    '& fieldset': { 
                                      borderColor: '#1E40AF',
                                      borderWidth: '2px'
                                    }
                                  }
                                },
                                '& .MuiInputLabel-root': { color: 'var(--text-secondary)' },
                                '& .MuiInputLabel-root.Mui-focused': { color: '#1E40AF' },
                                '& .MuiOutlinedInput-input': { 
                                  color: 'var(--text-primary)',
                                  background: 'transparent'
                                }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                              <InputLabel sx={{ color: 'var(--text-secondary)', '&.Mui-focused': { color: '#1E40AF' } }}>
                                Specialization
                              </InputLabel>
                              <Select
                  value={profileData.specialization}
                  onChange={(e) => setProfileData(prev => ({ ...prev, specialization: e.target.value }))}
                                label="Specialization"
                                sx={{
                                  background: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#FFFFFF',
                                  color: 'var(--text-primary)',
                                  '& fieldset': { 
                                    borderColor: 'var(--border-color)',
                                    borderWidth: '1.5px'
                                  },
                                  '&:hover fieldset': { 
                                    borderColor: '#1E40AF',
                                    borderWidth: '1.5px'
                                  },
                                  '&.Mui-focused': {
                                    background: isDarkMode ? 'rgba(30, 41, 59, 0.7)' : '#FFFFFF',
                                    '& fieldset': { 
                                      borderColor: '#1E40AF',
                                      borderWidth: '2px'
                                    }
                                  }
                                }}
                              >
                                <MenuItem value="General">General</MenuItem>
                                <MenuItem value="ICU">ICU</MenuItem>
                                <MenuItem value="Emergency">Emergency</MenuItem>
                                <MenuItem value="Pediatric">Pediatric</MenuItem>
                                <MenuItem value="Surgical">Surgical</MenuItem>
                                <MenuItem value="Cardiac">Cardiac</MenuItem>
                                <MenuItem value="Oncology">Oncology</MenuItem>
                                <MenuItem value="Psychiatric">Psychiatric</MenuItem>
                                <MenuItem value="Other">Other</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Years of Experience"
                  type="number"
                              inputProps={{ min: 0, max: 50 }}
                  value={profileData.yearsOfExperience}
                              onChange={(e) => setProfileData(prev => ({ ...prev, yearsOfExperience: parseInt(e.target.value) || 0 }))}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  background: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#FFFFFF',
                                  '& fieldset': { 
                                    borderColor: 'var(--border-color)',
                                    borderWidth: '1.5px'
                                  },
                                  '&:hover fieldset': { 
                                    borderColor: '#1E40AF',
                                    borderWidth: '1.5px'
                                  },
                                  '&.Mui-focused': {
                                    background: isDarkMode ? 'rgba(30, 41, 59, 0.7)' : '#FFFFFF',
                                    '& fieldset': { 
                                      borderColor: '#1E40AF',
                                      borderWidth: '2px'
                                    }
                                  }
                                },
                                '& .MuiInputLabel-root': { color: 'var(--text-secondary)' },
                                '& .MuiInputLabel-root.Mui-focused': { color: '#1E40AF' },
                                '& .MuiOutlinedInput-input': { 
                                  color: 'var(--text-primary)',
                                  background: 'transparent'
                                }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                              <InputLabel sx={{ color: 'var(--text-secondary)', '&.Mui-focused': { color: '#1E40AF' } }}>
                                Preferred Shift
                              </InputLabel>
                              <Select
                  value={profileData.shift}
                  onChange={(e) => setProfileData(prev => ({ ...prev, shift: e.target.value }))}
                                label="Preferred Shift"
                                sx={{
                                  background: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#FFFFFF',
                                  color: 'var(--text-primary)',
                                  '& fieldset': { 
                                    borderColor: 'var(--border-color)',
                                    borderWidth: '1.5px'
                                  },
                                  '&:hover fieldset': { 
                                    borderColor: '#1E40AF',
                                    borderWidth: '1.5px'
                                  },
                                  '&.Mui-focused': {
                                    background: isDarkMode ? 'rgba(30, 41, 59, 0.7)' : '#FFFFFF',
                                    '& fieldset': { 
                                      borderColor: '#1E40AF',
                                      borderWidth: '2px'
                                    }
                                  }
                                }}
                              >
                                <MenuItem value="morning">Morning</MenuItem>
                                <MenuItem value="afternoon">Afternoon</MenuItem>
                                <MenuItem value="night">Night</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          
                          {/* Submit Button */}
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' }, mt: 2 }}>
                              <Button 
                                type="submit" 
                                variant="contained" 
                                disabled={updating}
                                sx={{
                                  width: { xs: '100%', sm: 'auto' },
                                  minWidth: { sm: 200 },
                                  py: 1.5,
                                  px: 4,
                                  fontSize: '1rem',
                                  fontWeight: 700,
                                  background: 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
                                  textTransform: 'none',
                                  borderRadius: 2,
                                  boxShadow: '0 4px 12px -4px rgba(30, 64, 175, 0.4)',
                                  '&:hover': {
                                    background: 'linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)',
                                    boxShadow: '0 8px 16px -4px rgba(30, 64, 175, 0.5)',
                                    transform: 'translateY(-2px)'
                                  },
                                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                }}
                              >
                                {updating ? 'Updating...' : 'Update Profile'}
                              </Button>
                            </Box>
                          </Grid>
                        </Grid>
                      </form>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Right Column: Education & Certifications */}
                <Grid item xs={12} lg={4}>
                  <Stack spacing={3}>
                    {/* Education Card */}
                    <Card sx={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 3 }}>
                      <CardContent sx={{ p: 3 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <SchoolIcon sx={{ fontSize: 24, color: '#1E40AF' }} />
                            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.125rem', color: 'var(--text-primary)' }}>
                              Education
                            </Typography>
                          </Stack>
                          <Tooltip title="Add Education">
                            <IconButton 
                              onClick={addEducation}
                              size="small"
                              sx={{
                                bgcolor: 'rgba(30, 64, 175, 0.1)',
                                color: '#1E40AF',
                                '&:hover': {
                                  bgcolor: 'rgba(30, 64, 175, 0.2)',
                                  transform: 'scale(1.1)'
                                }
                              }}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>

                        {profileData.education.length === 0 ? (
                          <Box sx={{ textAlign: 'center', py: 3 }}>
                            <SchoolIcon sx={{ fontSize: 48, color: 'var(--text-tertiary)', mb: 1, opacity: 0.5 }} />
                            <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                              No education added yet
                            </Typography>
                          </Box>
                        ) : (
                          <List sx={{ p: 0 }}>
                {profileData.education.map((edu, index) => (
                              <ListItem
                                key={index}
                                sx={{
                                  p: 2,
                                  mb: 1.5,
                                  bgcolor: 'var(--bg-secondary)',
                                  borderRadius: 2,
                                  border: '1px solid var(--border-light)',
                                  flexDirection: 'column',
                                  alignItems: 'stretch'
                                }}
                              >
                                <TextField
                                  fullWidth
                      placeholder="Degree"
                      value={edu.degree}
                      onChange={(e) => updateEducation(index, 'degree', e.target.value)}
                                  size="small"
                                  sx={{ 
                                    mb: 1.5,
                                    '& .MuiOutlinedInput-root': {
                                      background: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#FFFFFF',
                                      '& fieldset': { borderColor: 'var(--border-color)', borderWidth: '1.5px' },
                                      '&:hover fieldset': { borderColor: '#1E40AF' },
                                      '&.Mui-focused': {
                                        background: isDarkMode ? 'rgba(30, 41, 59, 0.7)' : '#FFFFFF',
                                        '& fieldset': { borderColor: '#1E40AF', borderWidth: '2px' }
                                      }
                                    },
                                    '& .MuiOutlinedInput-input': { color: 'var(--text-primary)', background: 'transparent' }
                                  }}
                                />
                                <TextField
                                  fullWidth
                      placeholder="Institution"
                      value={edu.institution}
                      onChange={(e) => updateEducation(index, 'institution', e.target.value)}
                                  size="small"
                                  sx={{ 
                                    mb: 1.5,
                                    '& .MuiOutlinedInput-root': {
                                      background: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#FFFFFF',
                                      '& fieldset': { borderColor: 'var(--border-color)', borderWidth: '1.5px' },
                                      '&:hover fieldset': { borderColor: '#1E40AF' },
                                      '&.Mui-focused': {
                                        background: isDarkMode ? 'rgba(30, 41, 59, 0.7)' : '#FFFFFF',
                                        '& fieldset': { borderColor: '#1E40AF', borderWidth: '2px' }
                                      }
                                    },
                                    '& .MuiOutlinedInput-input': { color: 'var(--text-primary)', background: 'transparent' }
                                  }}
                                />
                                <Stack direction="row" spacing={1}>
                                  <TextField
                      placeholder="Year"
                      value={edu.year}
                      onChange={(e) => updateEducation(index, 'year', e.target.value)}
                                    size="small"
                                    sx={{ 
                                      flex: 1,
                                      '& .MuiOutlinedInput-root': {
                                        background: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#FFFFFF',
                                        '& fieldset': { borderColor: 'var(--border-color)', borderWidth: '1.5px' },
                                        '&:hover fieldset': { borderColor: '#1E40AF' },
                                        '&.Mui-focused': {
                                          background: isDarkMode ? 'rgba(30, 41, 59, 0.7)' : '#FFFFFF',
                                          '& fieldset': { borderColor: '#1E40AF', borderWidth: '2px' }
                                        }
                                      },
                                      '& .MuiOutlinedInput-input': { color: 'var(--text-primary)', background: 'transparent' }
                                    }}
                                  />
                                  <IconButton 
                                    onClick={() => removeEducation(index)}
                                    size="small"
                                    sx={{
                                      color: '#EF4444',
                                      '&:hover': {
                                        bgcolor: 'rgba(239, 68, 68, 0.1)'
                                      }
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                              </ListItem>
                            ))}
                          </List>
                        )}
                      </CardContent>
                    </Card>

                    {/* Certifications Card */}
                    <Card sx={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 3 }}>
                      <CardContent sx={{ p: 3 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <CardMembershipIcon sx={{ fontSize: 24, color: '#1E40AF' }} />
                            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.125rem', color: 'var(--text-primary)' }}>
                              Certifications
                            </Typography>
                          </Stack>
                          <Tooltip title="Add Certification">
                            <IconButton 
                              onClick={addCertification}
                              size="small"
                              sx={{
                                bgcolor: 'rgba(30, 64, 175, 0.1)',
                                color: '#1E40AF',
                                '&:hover': {
                                  bgcolor: 'rgba(30, 64, 175, 0.2)',
                                  transform: 'scale(1.1)'
                                }
                              }}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>

                        {profileData.certifications.length === 0 ? (
                          <Box sx={{ textAlign: 'center', py: 3 }}>
                            <CardMembershipIcon sx={{ fontSize: 48, color: 'var(--text-tertiary)', mb: 1, opacity: 0.5 }} />
                            <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                              No certifications added yet
                            </Typography>
                          </Box>
                        ) : (
                          <List sx={{ p: 0 }}>
                {profileData.certifications.map((cert, index) => (
                              <ListItem
                                key={index}
                                sx={{
                                  p: 0,
                                  mb: 1.5
                                }}
                              >
                                <TextField
                                  fullWidth
                                  placeholder="Certification Name"
                      value={cert}
                      onChange={(e) => updateCertification(index, e.target.value)}
                                  size="small"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      background: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#FFFFFF',
                                      '& fieldset': { borderColor: 'var(--border-color)', borderWidth: '1.5px' },
                                      '&:hover fieldset': { borderColor: '#1E40AF' },
                                      '&.Mui-focused': {
                                        background: isDarkMode ? 'rgba(30, 41, 59, 0.7)' : '#FFFFFF',
                                        '& fieldset': { borderColor: '#1E40AF', borderWidth: '2px' }
                                      }
                                    },
                                    '& .MuiOutlinedInput-input': { color: 'var(--text-primary)', background: 'transparent' }
                                  }}
                                  InputProps={{
                                    endAdornment: (
                                      <IconButton 
                                        onClick={() => removeCertification(index)}
                                        size="small"
                                        sx={{
                                          color: '#EF4444',
                                          '&:hover': {
                                            bgcolor: 'rgba(239, 68, 68, 0.1)'
                                          }
                                        }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    )
                                  }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        )}
                      </CardContent>
                    </Card>
                  </Stack>
                </Grid>
              </Grid>
            )}

            {/* Emergency Requests Tab (formerly tab 3, now tab 2) */}
            {activeTab === 2 && (
              <Box>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                  <EmergencyIcon sx={{ fontSize: 28, color: '#1E40AF' }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                    All Emergency Requests
                  </Typography>
                </Stack>
                
              {emergencyRequests.length === 0 ? (
                  <Card sx={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 3 }}>
                    <CardContent>
                      <Box sx={{ textAlign: 'center', py: 6 }}>
                        <EmergencyIcon sx={{ fontSize: 64, color: 'var(--text-tertiary)', mb: 2, opacity: 0.5 }} />
                        <Typography variant="h6" sx={{ color: 'var(--text-secondary)', mb: 1 }}>
                          No Emergency Requests
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'var(--text-tertiary)' }}>
                          You'll see new requests here when patients need your help
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                ) : (
                  <Grid container spacing={3}>
                  {emergencyRequests.map((request, index) => (
                      <Grid item xs={12} sm={6} md={4} key={request._id || index}>
                        <Card 
                          sx={{
                            height: '100%',
                            minHeight: 280,
                            borderLeft: `5px solid ${getUrgencyColor(request.urgency)}`,
                            borderRadius: 2,
                            background: 'var(--bg-card)',
                            border: '1px solid var(--border-color)',
                            borderLeftWidth: '5px',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                              transform: 'translateY(-6px)',
                              boxShadow: 'var(--shadow-xl)'
                            }
                          }}
                        >
                          <CardContent sx={{ p: 3 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-primary)' }}>
                                #{request._id ? request._id.slice(-8).toUpperCase() : (index + 1).toString().padStart(8, '0')}
                              </Typography>
                              <Chip 
                                label={`${request.urgency || 'Medium'} Priority`}
                                size="small"
                                sx={{
                                  height: 24,
                                  fontSize: '0.7rem',
                                  fontWeight: 700,
                                  bgcolor: `${getUrgencyColor(request.urgency)}15`,
                                  color: getUrgencyColor(request.urgency),
                                  border: `1px solid ${getUrgencyColor(request.urgency)}`,
                                  textTransform: 'uppercase'
                                }}
                              />
                            </Stack>
                            
                            <Divider sx={{ my: 2, borderColor: 'var(--border-color)' }} />
                            
                            <Stack spacing={1.5} sx={{ mb: 3 }}>
                              <Box>
                                <Typography variant="caption" sx={{ color: 'var(--text-tertiary)', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                                  Patient
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                                  {request.patientId?.name || 'Unknown'}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" sx={{ color: 'var(--text-tertiary)', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                                  Location
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                                  {request.location || 'Not specified'}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" sx={{ color: 'var(--text-tertiary)', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                                  Status
                                </Typography>
                                <Chip 
                                  label={request.status} 
                                  size="small"
                                  color={
                                    request.status === 'completed' ? 'success' :
                                    request.status === 'accepted' ? 'info' :
                                    request.status === 'pending' ? 'warning' : 'default'
                                  }
                                  sx={{ 
                                    height: 22,
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    textTransform: 'capitalize',
                                    mt: 0.5
                                  }}
                                />
                              </Box>
                            </Stack>

                            <Stack spacing={1.5}>
                        {request.status === 'pending' && (
                                <Button 
                                  variant="contained" 
                                  fullWidth
                                  startIcon={<CheckCircleIcon />}
                            onClick={() => handleAcceptRequest(request._id)}
                                  sx={{
                                    background: 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    py: 1,
                                    '&:hover': {
                                      background: 'linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)',
                                      transform: 'translateY(-2px)',
                                      boxShadow: '0 8px 16px -4px rgba(30, 64, 175, 0.4)'
                                    },
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                  }}
                          >
                            Accept Request
                                </Button>
                        )}
                        {request.status === 'accepted' && (
                                <Button 
                                  variant="contained" 
                                  color="success"
                                  fullWidth
                                  startIcon={<CheckCircleIcon />}
                            onClick={() => handleCompleteRequest(request._id)}
                                  sx={{
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    py: 1
                                  }}
                          >
                            Complete Service
                                </Button>
                              )}
                              <Button 
                                variant="outlined" 
                                fullWidth
                                startIcon={<VisibilityIcon />}
                                sx={{
                                  borderColor: 'var(--border-color)',
                                  color: 'var(--text-primary)',
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  py: 1,
                                  '&:hover': {
                                    borderColor: '#1E40AF',
                                    background: 'rgba(30, 64, 175, 0.08)'
                                  }
                                }}
                              >
                                View Details
                              </Button>
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            )}

            {/* Reviews Tab */}
            {activeTab === 3 && (
              <Box>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                  <ReviewsIcon sx={{ fontSize: 28, color: '#1E40AF' }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                    Patient Reviews
                  </Typography>
                </Stack>
                
                {reviewsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress sx={{ color: '#1E40AF' }} />
                  </Box>
                ) : reviews.length === 0 ? (
                  <Card sx={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 3 }}>
                    <CardContent>
                      <Box sx={{ textAlign: 'center', py: 6 }}>
                        <ReviewsIcon sx={{ fontSize: 64, color: 'var(--text-tertiary)', mb: 2, opacity: 0.5 }} />
                        <Typography variant="h6" sx={{ color: 'var(--text-secondary)', mb: 1 }}>
                          No Reviews Yet
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'var(--text-tertiary)' }}>
                          Reviews from patients will appear here once they rate your services
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                ) : (
                  <Grid container spacing={2.5}>
                    {reviews.map((review) => (
                      <Grid item xs={12} key={review._id}>
                        <Card 
                          sx={{
                            background: 'var(--bg-card)',
                            border: '1px solid var(--border-color)',
                            borderRadius: 3,
                            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                            '@media (prefers-reduced-motion: reduce)': {
                              transition: 'none'
                            },
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 8px 16px rgba(0, 0, 0, 0.12)',
                              borderColor: '#1E40AF'
                            }
                          }}
                        >
                          <CardContent sx={{ p: 3 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                                  {review.reviewer || 'Anonymous Client'}
                                </Typography>
                                <Chip 
                                  label="Verified Client"
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.7rem',
                                    bgcolor: 'rgba(16, 185, 129, 0.15)',
                                    color: '#10B981',
                                    border: '1px solid #10B981',
                                    fontWeight: 600
                                  }}
                                />
                              </Stack>
                              <Stack direction="row" spacing={0.5}>
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <StarIcon
                                    key={star}
                                    sx={{
                                      fontSize: 20,
                                      color: star <= review.rating ? '#FBBF24' : 'var(--text-tertiary)',
                                      opacity: star <= review.rating ? 1 : 0.3
                                    }}
                                  />
                                ))}
                              </Stack>
                            </Stack>
                            
                            {review.comment && (
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: 'var(--text-primary)',
                                  mb: 2,
                                  lineHeight: 1.6
                                }}
                              >
                                {review.comment}
                              </Typography>
                            )}
                            
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: 'var(--text-tertiary)',
                                fontSize: '0.75rem'
                              }}
                            >
                              {review.createdAt 
                                ? new Date(review.createdAt).toLocaleDateString('en-US', { 
                                    year: 'numeric', 
                                    month: 'long', 
                                    day: 'numeric' 
                                  })
                                : 'Date not available'}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            )}
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default NurseDashboard;
